// @ts-strict-ignore
import { getAbsoluteApiUrl } from "@dashboard/config";
import useNotifier from "@dashboard/hooks/useNotifier";
import { createFetch } from "@saleor/sdk";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const sdkFetch = createFetch();

export interface ExcludedProductType {
  slug: string;
  name: string;
}

interface MethodExclusions {
  name: string;
  excluded_product_slugs: string[];
  excluded_product_types: ExcludedProductType[];
}

interface ZoneData {
  name: string;
  methods: MethodExclusions[];
}

interface OverviewData {
  zones: ZoneData[];
}

export interface ExclusionsPatch {
  product_slugs?: string[];
  product_type_slugs?: string[];
}

function getBackendOrigin(): string {
  try {
    return new URL(getAbsoluteApiUrl()).origin;
  } catch {
    return "";
  }
}

function getOverviewUrl(): string {
  return `${getBackendOrigin()}/shipping-overview/`;
}

function getPatchUrl(): string {
  return `${getBackendOrigin()}/shipping-rate-exclusions/`;
}

/**
 * Imperatively persist a rate's exclusions. Returns the raw Response so callers
 * can decide how to surface errors. Used by the create flow (where the rate name
 * is only known after the rate is created) and by the hook below.
 */
export async function persistShippingRateExclusions(
  zoneName: string,
  rateName: string,
  patch: ExclusionsPatch,
): Promise<Response> {
  return sdkFetch(getPatchUrl(), {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify({ zone_name: zoneName, rate_name: rateName, ...patch }),
  } as RequestInit);
}

export function useShippingRateExclusions(zoneName: string, rateName: string) {
  const [productTypes, setProductTypes] = useState<ExcludedProductType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const notify = useNotifier();
  const intl = useIntl();

  useEffect(() => {
    if (!zoneName || !rateName) {
      setLoading(false);

      return;
    }

    setLoading(true);
    setError(null);
    sdkFetch(getOverviewUrl(), {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    } as RequestInit)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);

        return r.json() as Promise<OverviewData>;
      })
      .then(data => {
        const zone = data.zones.find(z => z.name === zoneName);
        const method = zone?.methods.find(m => m.name === rateName);

        setProductTypes(method?.excluded_product_types ?? []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [zoneName, rateName]);

  const save = async (patch: ExclusionsPatch, opts?: { silent?: boolean }) => {
    setSaving(true);

    try {
      const res = await persistShippingRateExclusions(zoneName, rateName, patch);

      if (!res.ok) {
        const text = await res.text().catch(() => "");

        notify({
          status: "error",
          text: intl.formatMessage(
            { id: "2i02Gn", defaultMessage: "Failed to save ({status}): {detail}" },
            { status: res.status, detail: text || res.statusText },
          ),
        });

        return false;
      }

      if (!opts?.silent) {
        notify({
          status: "success",
          text: intl.formatMessage({
            id: "7I2/ff",
            defaultMessage: "Excluded product types saved.",
          }),
        });
      }

      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);

      notify({ status: "error", text: msg });

      return false;
    } finally {
      setSaving(false);
    }
  };

  return { productTypes, loading, error, saving, save };
}
